import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Location } from "history";
import { Box, Flex } from "rebass/styled-components";
import styled, { css } from "styled-components";
import videojs from "video.js";
import { FaChevronUp, FaChevronDown, FaPlay } from "react-icons/fa";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { CountdownCircle } from "MVVM/Views/EmbeddablePlaylist/Components/CountdownCircle";
import { ClipExperienceViewModel } from "./ViewModel";
import { VideoPlayer } from "MVVM/VideoPlayer";
import { countClipImpression, isExternalPartner } from "MVVM/Utilities/Clip";
import { addViewedClip } from "MVVM/Utilities/ViewedClips";
import CloseButton from "components/atoms/form/button/close";
import { Button } from "MVVM/Components/Button";
import { EClipPageSource, IClip, TClip } from "MVVM/@types";
import { ClipPruned } from "MVVM/Components/Clip/Components/ClipPruned";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { useLoginModal } from "MVVM/Hooks/useLoginModal";
import { useUser } from "MVVM/Hooks/useUser";
import { usePopulateCompetitionEligibility } from "MVVM/Hooks/useCompetitionEligibility";
import { useWindowSize } from "MVVM/Hooks/useWindowSize";
import { analytics } from "MVVM/Analytics";
import { ViewableClip, LoadingClipId } from "MVVM/State/clip";
import { ClipFullModalState } from "MVVM/State/modals";
import { ClipExperienceSidebarSelectedTab } from "MVVM/State/sidebar";
import { Sidebar } from "./Sidebar";
import { Missing } from "./Missing";
import { CareerMontagePending } from "./CareerMontagePending";
import { ClipPending } from "./ClipPending";
import { etaFromIso } from "MVVM/Utilities/approxTimeDisplay";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { useIsFirstLoad } from "MVVM/Hooks/useIsFirstLoad";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SubscribeToPlayer } from "./SubscribePlayerModal";
import { DownloadLoginModal } from "MVVM/Views/MatchHistory/Components/Shared/DownloadLoginModal";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import theme from "theme/themeThree";
import { ClipContext } from "./@types";
import { useAds } from "MVVM/Providers";
import { AdUnit } from "MVVM/Components/Ads";
import { SavedClipStatusAtom } from "MVVM/State/storagePolicy";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { ClipQualityUpsell } from "./ClipQualityUpsell";
import { AllstarClipBubble, isAllstarClip } from "./AllstarClipBubble";

interface ClipExperienceProps {
  clipId?: string;
  source?: EClipPageSource;
  orientation?: string;
  clipContext?: ClipContext | null;
}

const ClipExperience = ({
  clipId = "",
  source = EClipPageSource.DIRECT,
  orientation = "LANDSCAPE",
  clipContext,
}: ClipExperienceProps) => {
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId?.split("?")[0] || rawId;
  const mobileView = getQueryParam("mobileView");
  const videoPlayerRef = useRef<videojs.Player | null>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const { width: windowWidth } = useWindowSize();
  const history = useHistory();
  const location = useLocation<{
    autoplay: boolean | undefined;
    referrer: string | undefined;
  }>();

  const referrer = location.state?.referrer;
  const {
    data,
    error,
    loading,
    refetch,
    createPrunedClip,
    feedData,
    feedError,
    feedLoading,
    getNextClip,
    timeline,
    context: urlContext,
  } = ClipExperienceViewModel({
    id: id || clipId,
  });
  const context = clipContext || urlContext;
  const isModal = context?.metadata?.source === "modal";
  const { allstarUser } = useUser();
  usePopulateCompetitionEligibility();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const savedClipStatus = useRecoilValue(SavedClipStatusAtom);
  const isFirstLoad = useIsFirstLoad();
  const mobileBreakpoint = parseInt(theme.breaks.theater);
  const isMobileSize = windowWidth && windowWidth <= mobileBreakpoint;
  const playerName = getQueryParam("playerName");
  const playerPhoto = getQueryParam("playerPhoto");
  const teamPhoto = getQueryParam("teamPhoto");
  const subscribeToHltvClipsQueryParam = getQueryParam(
    "subscribe_to_hltv_clips",
  );
  const saveQueryParam = getQueryParam("save");
  const clickSource = getQueryParam("clickSource");
  const currentClip = useRecoilValue(ViewableClip);
  const setViewableClip = useSetRecoilState(ViewableClip);
  const setLoadingClipId = useSetRecoilState(LoadingClipId);
  const selectedTabName = useRecoilValue(ClipExperienceSidebarSelectedTab);
  const [nextClipPreview, setNextClipPreview] = useState<TClip | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [gameAdShowing, setGameAdShowing] = useState(false);
  const [countdownActive, setCountdownActive] = useState(false);
  const [endSlateHasAd, setEndSlateHasAd] = useState<boolean | null>(null);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [initialSlateHasAd, setInitialSlateHasAd] = useState<boolean | null>(
    null,
  );
  const [initialSlateDismissed, setInitialSlateDismissed] = useState(false);
  const [showSlideInAd, setShowSlideInAd] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const [pauseGridHasAd, setPauseGridHasAd] = useState(false);
  const {
    config: {
      video: { enabled: videoAdsEnabled },
      display: { enabled: displayAdsEnabled },
    },
    imaStatus,
    gptStatus,
  } = useAds();

  const adApplicableUser =
    ["FREE", "INTERNAL", "ESSENTIALS"].includes(
      allstarUser?.user?.userGroup?.type,
    ) || !allstarUser?.loggedIn;
  const enableVideoAds = videoAdsEnabled && adApplicableUser;
  const enableDisplayAds = displayAdsEnabled && adApplicableUser;

  const shouldShowInitialSlateAd = useMemo(
    () =>
      enableDisplayAds &&
      !hasPlayedOnce &&
      initialSlateHasAd !== false &&
      !initialSlateDismissed &&
      imaStatus === "ready" &&
      gptStatus === "ready",
    [
      enableDisplayAds,
      hasPlayedOnce,
      initialSlateHasAd,
      initialSlateDismissed,
      imaStatus,
      gptStatus,
    ],
  );

  const nextClipContentRef = useRef<HTMLDivElement>(null);
  const shouldUseMobileVersion =
    mobileView === "true" ||
    (isMobileSize && !mobileView) ||
    orientation === "PORTRAIT";
  const getVideoSource = (clip: IClip) =>
    shouldUseMobileVersion && clip.portraitUrl ? clip.portraitUrl : clip.url;

  const options = { game: getMatchHistoryGameEnum(data?.clip?.game) || 0 };
  const { portalOpen, popModal, Portal } = useLoginModal(
    allstarUser,
    clickSource === "reactions" || clickSource === "comments" ? 5000 : 0,
    data?.clip,
    undefined,
    options,
  );

  const [, setVideosWatchedCount] = useState(0);

  const {
    portalOpen: subscribePlayerModalOpen,
    Portal: SubscribePlayerModalPortal,
    setPortalOpen: setSubscribePlayerModalOpen,
  } = usePortal(
    <SubscribeToPlayer
      playerName={playerName || ""}
      playerPhoto={playerPhoto || ""}
      teamPhoto={teamPhoto || ""}
    />,
  );

  const {
    portalOpen: downloadModalOpen,
    Portal: DownloadModalPortal,
    setPortalOpen: setDownloadModalOpen,
  } = usePortal(
    <DownloadLoginModal
      game={getMatchHistoryGameEnum(data?.clip?.game) || 0}
      clip={data?.clip}
      moment={data?.clip?.moment}
      matchTitle={data?.clip?.title}
    />,
  );

  const handleClose = useCallback(() => {
    analytics.track("Clip Page - Close Button Clicked", {});
    referrer ? history.push(referrer) : history.goBack();
  }, [referrer, history]);

  const showNextClipPreview = useCallback(() => {
    const aPortalIsOpen =
      downloadModalOpen || subscribePlayerModalOpen || portalOpen;

    if (
      !feedData?.clipsRecommended?.length ||
      !currentClip ||
      aPortalIsOpen ||
      selectedTabName !== "recommended"
    )
      return;

    const currentIndex = feedData.clipsRecommended.findIndex(
      (clip) => clip.shareId === currentClip.shareId,
    );

    const nextClip = feedData.clipsRecommended[currentIndex + 1];

    if (!nextClip) return;

    setNextClipPreview(nextClip);
    setCountdown(5);
    setCountdownActive(true);
  }, [
    feedData,
    currentClip,
    downloadModalOpen,
    subscribePlayerModalOpen,
    portalOpen,
    selectedTabName,
  ]);

  const handleClosePreview = useCallback(() => {
    setNextClipPreview(null);
    setCountdownActive(false);
    setEndSlateHasAd(null);
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        nextClipContentRef.current &&
        !nextClipContentRef.current.contains(event.target as Node)
      ) {
        handleClosePreview();
      }
    },
    [handleClosePreview],
  );

  const handleAutoPlayNext = useCallback(async () => {
    if (
      !feedData?.clipsRecommended?.length ||
      !currentClip ||
      selectedTabName !== "recommended"
    )
      return;

    const currentIndex = feedData.clipsRecommended.findIndex(
      (clip) => clip.shareId === currentClip.shareId,
    );
    const nextClip = feedData.clipsRecommended[currentIndex + 1];
    if (!nextClip) return;
    try {
      analytics.track("Clip Page - Auto Play Next Clip", {});

      setLoadingClipId(nextClip.shareId);
      const result = await getNextClip({ variables: { id: nextClip.shareId } });

      if (result.data?.clip) {
        setViewableClip(result.data.clip);

        const currentSearch = window.location.search;
        window.history.pushState(
          {},
          "",
          `/clip/${result.data.clip.shareId}${currentSearch}`,
        );
      }
    } catch (e) {
      // Handle error silently
    } finally {
      setLoadingClipId(null);
      setNextClipPreview(null);
    }
  }, [
    feedData,
    currentClip,
    setViewableClip,
    setLoadingClipId,
    getNextClip,
    selectedTabName,
  ]);

  useEffect(() => {
    if (!countdownActive || !nextClipPreview) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCountdownActive(false);
          handleAutoPlayNext();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [countdownActive, nextClipPreview, handleAutoPlayNext]);

  useEffect(() => {
    if (!nextClipPreview) return;

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [nextClipPreview, handleClickOutside]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        [EClipPageSource.FEED, EClipPageSource.DIRECT].includes(source) &&
        !isFirstLoad
      ) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [source, isFirstLoad, handleClose]);

  useEffect(() => {
    if (playerName || subscribeToHltvClipsQueryParam) {
      setSubscribePlayerModalOpen(true);
    }
  }, [playerName, subscribeToHltvClipsQueryParam, setSubscribePlayerModalOpen]);

  useEffect(() => {
    if (saveQueryParam === "true" && !allstarUser.user) {
      setDownloadModalOpen(true);
    }
  }, [saveQueryParam, allstarUser, setDownloadModalOpen]);

  useEffect(() => {
    if (hasPlayedOnce && currentClip && videoPlayerRef.current) {
      const player = videoPlayerRef.current;
      const playAttempt = setTimeout(() => {
        player.play()?.catch(() => {});
      }, 100);
      return () => clearTimeout(playAttempt);
    }
  }, [currentClip?.shareId, hasPlayedOnce]);

  useEffect(() => {
    if (hasPlayedOnce && enableDisplayAds && !showSlideInAd) {
      const timer = setTimeout(() => {
        setShowSlideInAd(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasPlayedOnce, enableDisplayAds, showSlideInAd]);

  const handlePlayEnd = () => {
    showNextClipPreview();
  };

  if (loading)
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        height="50vh"
        width="100%"
      >
        <LoadingSpinner height={75} width={75} />
      </Flex>
    );
  if (
    error ||
    !data.clip ||
    ["Deleted", "Inactive", "DELETED", "INACTIVE"].includes(data.clip?.status)
  ) {
    analytics.track("Clip Experience - Missing");
    return <Missing />;
  }

  window.history.replaceState({}, "");

  const { clip } = data;

  const atomSavedStatus = savedClipStatus[clip.internalId];
  const isClipSaved =
    atomSavedStatus !== undefined
      ? atomSavedStatus
      : (clip.saved?.savedBy?.length ?? 0) > 0;
  const isUsersClip = clip.user?._id === allstarUser?.user?._id;
  const isCareerMontage = clip.type === "CAREER_MONTAGE";
  const isFullMatchVod = clip.type === "FULL_MATCH_VOD";
  const hasPartner = isExternalPartner(clip.developer);
  const showExpirationBanner =
    showSubscriptionOverhaul &&
    !isClipSaved &&
    !hasPartner &&
    !isCareerMontage &&
    !isFullMatchVod &&
    clip.status === "READY";

  return (
    <ClipWrapper
      isModal={isModal}
      style={
        clip?.thumbnailStandardUrl || timeline?.clip?.thumbnailUrl
          ? {
              backgroundImage: `url(${
                clip?.thumbnailStandardUrl ?? timeline?.clip?.thumbnailUrl
              })`,
            }
          : undefined
      }
    >
      {portalOpen && Portal}
      {subscribePlayerModalOpen && SubscribePlayerModalPortal}
      {downloadModalOpen && DownloadModalPortal}
      {[EClipPageSource.FEED, EClipPageSource.DIRECT].includes(source) &&
        !isFirstLoad && (
          <StyledCloseButton
            size="small"
            classification="round"
            onClick={handleClose}
          />
        )}
      <ClipContainer isModal={isModal}>
        <VideoContainer isModal={isModal}>
          <VideoWrapper mobileView={!!mobileView} ref={videoWrapperRef}>
            {[EClipPageSource.FEED].includes(source) && (
              <>
                <StyledButtonUp
                  size="small"
                  classification="round"
                  onClick={history.goBack}
                  flex={true}
                >
                  <FaChevronUp />
                </StyledButtonUp>
                <StyledButtonDown
                  size="small"
                  classification="round"
                  onClick={history.goBack}
                  flex={true}
                >
                  <FaChevronDown />
                </StyledButtonDown>
              </>
            )}
            {["ON_DEMAND", "PRUNED"].includes(clip.status) && (
              <Flex
                width="100%"
                height="100%"
                justifyContent="center"
                alignItems="center"
              >
                <ClipPruned
                  onClick={() => {
                    createPrunedClip({
                      variables: {
                        clipIdentifier: clip.shareId,
                      },
                    });
                  }}
                  clipTitle={clip.title}
                  onDemand={clip.status === "ON_DEMAND"}
                />
              </Flex>
            )}
            {clip.status === "PENDING" && (
              <Flex
                width="100%"
                height="100%"
                justifyContent="center"
                alignItems="center"
              >
                {isCareerMontage || isFullMatchVod ? (
                  <CareerMontagePending
                    title={clip.title}
                    label={isFullMatchVod ? "Match VOD" : "Career Montage"}
                    eta={
                      timeline?.desktop?.estimatedCompletionAt
                        ? etaFromIso(timeline.desktop.estimatedCompletionAt)
                        : null
                    }
                  />
                ) : (
                  <ClipPending
                    title={clip.title}
                    game={clip.game}
                    timeline={timeline}
                  />
                )}
              </Flex>
            )}
            {clip.status === "READY" && (
              <>
                <VideoPlayer
                  id="video_wrapper"
                  mp4Source={getVideoSource(clip)}
                  refForVideoPlayer={videoPlayerRef}
                  onAdStarted={() => setGameAdShowing(true)}
                  onAdCompleted={() => setGameAdShowing(false)}
                  onMuteChange={(muted) => {
                    // Switch off "muted" autoplay strategy once user unmutes — otherwise it re-mutes on every loadstart (next clip). "any" falls back to muted if the browser blocks unmuted.
                    const player = videoPlayerRef.current;
                    if (!muted && player && player.autoplay() === "muted") {
                      player.autoplay("any");
                    }
                  }}
                  onPlayStart={() => {
                    countClipImpression(clip.shareId);
                    addViewedClip(clip.internalId);
                    if (!hasPlayedOnce) setHasPlayedOnce(true);
                    setIsVideoPaused(false);
                  }}
                  onPlay={() => setIsVideoPaused(false)}
                  onPlayPause={() => setIsVideoPaused(true)}
                  onPlayEnd={(player) => {
                    popModal() && setTimeout(() => player?.pause(), 500);
                    handlePlayEnd();
                  }}
                  loop={selectedTabName !== "recommended"}
                  playAnalyticsProperties={{
                    shareId: clip.shareId,
                    clipId: clip._id,
                    clipOwner: clip.user,
                    total_views: clip.views,
                    game: clip.game,
                    clipType: clip.type,
                    ...(context?.type === "profile" &&
                    context.metadata?.viewerContext
                      ? { viewerContext: context.metadata.viewerContext }
                      : {}),
                  }}
                  watchAnalyticsProperties={{
                    clipId: clip._id,
                    collection: "Clip Experience",
                    collectionType: "Generic",
                    item: clip.shareId,
                    itemType: "Clip",
                    action: "watch",
                    clipType: clip.type,
                    game: clip.game,
                  }}
                  options={{
                    autoplay:
                      isModal ||
                      context?.type === "profile" ||
                      context?.type === "pinned-clips"
                        ? "play"
                        : hasPlayedOnce
                        ? "play"
                        : getAutoPlayState(location, clip, enableVideoAds),
                    controls: true,
                    fluid: true,
                    muted:
                      getQueryParam("muted") === "false" ? false : undefined,
                    poster: clip.thumbnailStandardUrl,
                  }}
                  duration={clip.duration}
                  ads={enableVideoAds}
                />
                {/* one bubble at a time: !allstar clips show the !allstar
                    bubble, everything else can show the quality upsell */}
                {isAllstarClip(clip) ? (
                  <AllstarClipBubble clip={clip} />
                ) : (
                  <ClipQualityUpsell
                    clip={clip}
                    videoPlayerRef={videoPlayerRef}
                  />
                )}
                {shouldShowInitialSlateAd && (
                  <InitialSlateAd
                    game={clip.game}
                    onAdLoaded={setInitialSlateHasAd}
                    containerRef={videoWrapperRef}
                    onDismiss={() => {
                      setInitialSlateDismissed(true);
                    }}
                  />
                )}
                {hasPlayedOnce && !isVideoPaused && (
                  <>
                    <TopLeftBugContainer $visible={true}>
                      <AdUnit
                        slotId="clip-experience-top-left-bug"
                        adUnitType="player_top_left_bug"
                        game={clip.game}
                        collapseIfEmpty
                        rounded
                      />
                    </TopLeftBugContainer>
                    <SlideInAdContainer $visible={showSlideInAd}>
                      <AdUnit
                        slotId="clip-experience-slide-in"
                        adUnitType="mobile-leaderboard"
                        game={clip.game}
                        rounded
                        containerRef={videoWrapperRef}
                      />
                    </SlideInAdContainer>
                  </>
                )}
                {!isVideoPaused && (
                  <LowerVideoPlayerBannerContainer>
                    <AdUnit
                      slotId="clip-experience-banner-bottom"
                      adUnitType="lower_video_player_banner"
                      game={clip.game}
                      rounded
                      containerRef={videoWrapperRef}
                      dismissible
                    />
                  </LowerVideoPlayerBannerContainer>
                )}
              </>
            )}
            {isVideoPaused && hasPlayedOnce && (
              <PauseAdGridOverlay
                $hasAd={pauseGridHasAd}
                onClick={() => {
                  videoPlayerRef.current?.play();
                }}
              >
                <Box onClick={(e) => e.stopPropagation()}>
                  <AdUnit
                    slotId="clip-experience-video-slate"
                    adUnitType="video_slate_single"
                    game={clip.game}
                    onAdLoaded={(hasAd) => hasAd && setPauseGridHasAd(true)}
                    rounded
                    containerRef={videoWrapperRef}
                    showUpgradeCta
                  />
                </Box>
              </PauseAdGridOverlay>
            )}
            {nextClipPreview && (
              <NextClipOverlay>
                <NextClipContent ref={nextClipContentRef}>
                  <ThumbnailContainer onClick={handleAutoPlayNext}>
                    {!enableDisplayAds ||
                    endSlateHasAd === false ||
                    imaStatus === "error" ? (
                      <>
                        <ThumbnailImage
                          src={nextClipPreview.thumbnailStandardUrl}
                          alt={nextClipPreview.title}
                        />
                        <PlayIcon />
                      </>
                    ) : (
                      <AdUnit
                        slotId="clip-experience-end-slate"
                        adUnitType="video_slate_single"
                        game={clip.game}
                        onAdLoaded={setEndSlateHasAd}
                        rounded
                        containerRef={videoWrapperRef}
                      />
                    )}
                  </ThumbnailContainer>
                  <ClipInfo>
                    <LeftContent>
                      <NextUpText>Next Up:</NextUpText>
                      <ClipTitle>{nextClipPreview.title}</ClipTitle>
                    </LeftContent>
                    <CountdownContainer>
                      <CountdownCircle
                        countdown={countdown}
                        max={5}
                        size={windowWidth && windowWidth < 600 ? 30 : 50}
                      />
                    </CountdownContainer>
                  </ClipInfo>
                </NextClipContent>
              </NextClipOverlay>
            )}
          </VideoWrapper>
        </VideoContainer>

        <Sidebar
          clip={clip}
          context={context}
          referrer={referrer}
          feedData={feedData}
          feedError={feedError}
          feedLoading={feedLoading}
          onRemixClicked={() => {
            videoPlayerRef.current?.pause();
          }}
          showExpirationBanner={showExpirationBanner}
          isOwner={isUsersClip}
          developer={hasPartner ? clip.developer : undefined}
        />
      </ClipContainer>
    </ClipWrapper>
  );
};

const InitialSlateAd = ({
  game,
  onAdLoaded,
  onDismiss,
  containerRef,
}: {
  game: string;
  onAdLoaded: (hasAd: boolean) => void;
  onDismiss: () => void;
  containerRef?: React.RefObject<HTMLElement>;
}) => {
  return (
    <InitialSlateOverlay>
      <AdUnit
        slotId="clip-experience-initial-slate"
        adUnitType="video_slate_single"
        game={game}
        onAdLoaded={onAdLoaded}
        rounded
        containerRef={containerRef}
      />
    </InitialSlateOverlay>
  );
};

const getAutoPlayState = (
  location: Location<{
    autoplay: boolean | undefined;
    referrer: string | undefined;
  }>,
  clip: IClip,
  ads: boolean,
) => {
  const autoplayState = location?.state?.autoplay;
  const search = new URLSearchParams(window.location.search);
  const saveQueryParam = search.get("save");
  const editQueryParam = search.get("edit");
  const shareQueryParam = search.get("share");
  const remixQueryParam = search.get("remix");
  const playerQueryParam = search.get("playerName");
  const subscribeToHltvClipsQueryParam = search.get("subscribe_to_hltv_clips");
  const mutedQueryParam = search.get("muted");

  const shouldPreventAutoplayForRemix =
    remixQueryParam && clip.isRemixable && clip.moment?.id;

  if (
    saveQueryParam ||
    editQueryParam ||
    shareQueryParam ||
    playerQueryParam ||
    shouldPreventAutoplayForRemix ||
    subscribeToHltvClipsQueryParam
  ) {
    return false;
  }

  if (autoplayState) {
    return "play";
  }

  // muted=false: switch from "muted" autoplay strategy (which force-mutes) to "any" — tries unmuted, falls back to muted on browser block.
  if (mutedQueryParam === "false") {
    return ads ? false : "any";
  }

  return ads ? false : "muted";
};

const PinnedAdContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100px;

  z-index: 10;
`;

const SlideInAdContainer = styled(Box)<{ $visible: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

const TopLeftBugContainer = styled(Box)<{ $visible: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  display: ${({ $visible }) => ($visible ? "block" : "none")};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  mix-blend-mode: screen;
`;

const LowerVideoPlayerBannerContainer = styled(Box)`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9;
  pointer-events: auto;
`;

const PauseAdGridOverlay = styled(Box)<{ $hasAd: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $hasAd }) =>
    $hasAd ? "rgba(0, 0, 0, 0.7)" : "transparent"};
  z-index: 10;
  margin-bottom: 30px;
  pointer-events: ${({ $hasAd }) => ($hasAd ? "auto" : "none")};
`;

const AdDesktop = styled(Box)`
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
  bottom: 15px;
  left: 50%;
  overflow: hidden;
  padding: 0 20px;
  pointer-events: auto;
  position: absolute;
  transform: translateX(-50%);
  width: 100%;
  z-index: 10;

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    display: none;
  }
`;

const VideoWrapper = styled(Box)<{ mobileView?: boolean }>`
  position: relative;
  z-index: 1;

  ${(props) =>
    props.mobileView
      ? `
        aspect-ratio: 9/16;
        height: 100%;
        max-height: fit-content;
        margin: 0 auto;
        width: auto;

        .video-js {
          aspect-ratio: 9/16;
          height: 100% !important;
          max-height: calc(100vh - var(--site-header-height, 75px)) !important;
          width: auto !important;

          @media (max-width: ${props.theme.breaks.theater}) {
            height: 60vh !important;
          }
        }

        video {
          object-fit: contain !important;
        }
        `
      : `
        height: auto;
        width: 100%;
        position: relative;
        aspect-ratio: 16 / 9;
      `}
`;

const ClipWrapper = styled(Box)<{ isModal?: boolean }>`
  background-size: cover;
  height: 100%;
  max-height: calc(100vh - var(--site-header-height, 73px));
  overflow: hidden;
  position: relative;
  scrollbar-width: none;
  width: 100%;

  ${(props) => (props.isModal ? "border-top-left-radius: 8px;" : "")}
  ${(props) => (props.isModal ? "border-bottom-left-radius: 8px;" : "")}

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    background-color: ${({ theme }) => theme.colors.darkerNed};
    background-image: none !important;
    overflow: auto;
  }
`;

const ClipContainer = styled(Flex)<{ isModal?: boolean }>`
  backdrop-filter: blur(14px);
  height: 100%;
  width: 100%;

  ${(props) => (props.isModal ? "border-top-left-radius: 8px;" : "")}
  ${(props) => (props.isModal ? "border-bottom-left-radius: 8px;" : "")}

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    flex-direction: column;
    height: auto;
  }
`;

const roundButtonStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
  left: 50%;
  opacity: 0.8;
  position: absolute;
  transform: translate(-50%, 0%);
  z-index: 2;

  :hover {
    background: ${({ theme }) => theme.colors.darkerNed};
    color: ${({ theme }) => theme.colors.ice};
    opacity: 0.6;
  }
`;

const StyledButtonUp = styled(Button)`
  ${roundButtonStyles}
  top: 12px;
`;

const StyledButtonDown = styled(Button)`
  ${roundButtonStyles}
  bottom: 12px;
`;

const StyledCloseButton = styled(CloseButton)`
  left: 12px;
  position: absolute;
  top: 12px;
  z-index: 2;

  :hover {
    background: ${({ theme }) => theme.colors.darkerNed};
    color: ${({ theme }) => theme.colors.ice};
  }

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    display: none;
  }
`;

const CountdownContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  margin-left: 20px;
`;

const VideoContainer = styled(Flex)<{ isModal?: boolean }>`
  align-items: center;
  flex-grow: 1;
  justify-content: center;
  position: relative;

  ::before {
    background-color: rgba(0, 0, 0, 0.6);
    content: "";
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 0;

    ${(props) => (props.isModal ? "border-top-left-radius: 8px;" : "")}
    ${(props) => (props.isModal ? "border-bottom-left-radius: 8px;" : "")}
  }
`;

const NextClipOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  pointer-events: none;
  margin-bottom: 30px;
`;

const NextClipContent = styled(Flex)`
  flex-direction: column;
  align-items: center;
  width: 50%;
  max-width: 500px;
  padding: 30px;
  pointer-events: auto;
  padding-top: 30px;
`;

const ThumbnailContainer = styled(Box)`
  position: relative;

  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  cursor: pointer;
`;

const InitialSlateOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 16/9;
  object-fit: cover;
  display: block;
`;

const PlayIcon = styled(FaPlay)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  color: rgba(255, 255, 255, 0.9);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5));
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    width: 20px;
    height: 20px;
  }
`;

const ClipInfo = styled(Flex)`
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  color: white;
  width: 100%;
  margin-top: 20px;
`;

const LeftContent = styled(Flex)`
  flex-direction: column;
  flex: 1;

  gap: 5px;
`;

const NextUpText = styled(Box)`
  color: ${({ theme }) => theme.colors.iceAlpha60};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    font-size: 12px;
    line-height: 16px;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    font-size: 10px;
    line-height: 14px;
  }
`;

const ClipTitle = styled(Box)`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ice};
  line-height: 24px;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    font-size: 16px;
    line-height: 20px;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    font-size: 10px;
    line-height: 14px;
  }
`;

export default ClipExperience;
